import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/context/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import NewsletterPopup from '@/components/NewsletterPopup';
import Home from '@/pages/Home';
import Artists from '@/pages/Artists';
import Videos from '@/pages/Videos';
import Newsletters from '@/pages/Newsletters';
import Shop from '@/pages/Shop';
import Contact from '@/pages/Contact';
import Admin from '@/pages/Admin';

class ErrorBoundary extends React.Component {
  state = { err: false };
  static getDerivedStateFromError() { return { err: true }; }
  render() {
    if (this.state.err) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-brayo-paper px-6 text-center">
          <div>
            <p className="display-title text-3xl text-brayo-charcoal">Something went wrong</p>
            <button
              data-testid="error-reload-btn"
              onClick={() => window.location.reload()}
              className="meta-label mt-6 border border-brayo-line px-6 py-3 text-brayo-blue hover:border-brayo-blue"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  return (
    <div className="grain min-h-screen bg-brayo-paper text-brayo-charcoal">
      {!isAdmin && <Header />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/artists" element={<Artists />} />
          <Route path="/videos" element={<Videos />} />
          <Route path="/newsletters" element={<Newsletters />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <NewsletterPopup />}
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ScrollManager />
          <Shell />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: { background: '#FFFFFF', border: '1px solid #E2E0D8', color: '#161616', fontFamily: 'Archivo, sans-serif', fontSize: '12px', letterSpacing: '0.05em' },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}